import { useCallback, useEffect, useRef } from 'react';

import type {
  ApiEventId,
  ApiInternshipId,
  ApiJobId,
  ApiOrgId,
  ApiVolopId,
} from '#/api/types/ApiTypedId';
import {
  useActionsWhileLoggedOutProcessedAt,
  useLoggedIn,
} from '#/contexts/UserEnvironmentContext';
import { useKeyedState } from '#/hooks/useKeyedState';
import { useLatest } from '#/hooks/useLatest';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { SearchType } from '#/modules/search/types/SearchType';
import type { SearchSavedListingsStore } from '#/modules/search/zustand-stores/searchSavedListingsStore';
import { userDashboardApiClientFetchListingExtras } from '#/modules/userDashboard/api/listingExtras/userDashboardApiFetchListingExtras';
import { userDashboardApiCreateSavedListing } from '#/modules/userDashboard/api/savedListing/userDashboardApiCreateSavedListing';
import { userDashboardApiDeleteSavedListing } from '#/modules/userDashboard/api/savedListing/userDashboardApiDeleteSavedListing';
import { trackClicked } from '#/utils/analytics/track/trackClicked';
import { LISTING_TYPE_HUMAN_NAME_MAP } from '#/utils/constants/general/listingTypeHumanNameMap';
import { deferWithLoadingAnimation } from '#/utils/ui/routeLoadingAnimation';
import { showModal } from '#/zustand-stores/modalStore';

export type SaveListingType = (
  e?: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
) => Promise<void>;

type UseSaveListingArgs = {
  listingType: SearchType | 'IMPORTED';
  listingId:
    | ApiJobId
    | ApiInternshipId
    | ApiVolopId
    | ApiEventId
    | ApiOrgId
    | null;
  /**
   * When initiallySaved is null it means we're still loading it
   */
  initiallySaved: boolean | null;
  orgId: ApiOrgId | null;
  orgType: string | null;
  onSaved: (() => void) | null;
  onUnsaved: (() => void) | null;
  trackClickEvent: boolean;
  /**
   * This hooks syncs save/unsave to and from the store
   */
  searchSavedListingsStore: SearchSavedListingsStore | null;
};

export function useSaveListing({
  listingType,
  listingId,
  initiallySaved,
  orgId,
  orgType,
  onSaved,
  onUnsaved,
  trackClickEvent,
  searchSavedListingsStore,
}: UseSaveListingArgs) {
  const { loggedIn } = useLoggedIn();
  const initiallyLoggedInRef = useRef(loggedIn);
  const { i18n } = useI18n();

  const { actionsWhileLoggedOutProcessedAt } =
    useActionsWhileLoggedOutProcessedAt();
  const previousActionsWhileLoggedOutProcessedAt = useRef(
    actionsWhileLoggedOutProcessedAt,
  );

  const [saved, setSaved] = useKeyedState(Boolean(initiallySaved), {
    key: initiallySaved === null ? 'loading' : (listingId ?? ''),
  });
  const latestSavedRef = useLatest(saved);

  const saveAsGuest = useCallback(() => {
    if (!listingId) return; // Skip if listingId is null

    showModal('EMAIL_SIGNUP', {
      title: `Save this ${LISTING_TYPE_HUMAN_NAME_MAP(i18n)[listingType]}`,
      tmpStorage: {
        type: 'savedListing',
        savedListing: { id: listingId, type: listingType },
      },
      redirectTo: `${window.location.pathname}${window.location.search}`,
    });
  }, [listingId, listingType, i18n]);

  const save: SaveListingType = useCallback(async () => {
    if (!listingId) return; // Skip if listingId is null

    if (trackClickEvent) {
      trackClicked('Save Listing', {
        listing_type: LISTING_TYPE_HUMAN_NAME_MAP(i18n)[listingType],
        listing_id: listingId,
        org_id: orgId,
        org_type: orgType,
      });
    }

    if (!loggedIn) return saveAsGuest();

    await userDashboardApiCreateSavedListing(listingId, listingType);
    onSaved?.();
    setSaved(true);
  }, [
    listingId,
    trackClickEvent,
    loggedIn,
    saveAsGuest,
    listingType,
    onSaved,
    setSaved,
    orgId,
    orgType,
    i18n,
  ]);

  const unsave = useCallback(async () => {
    if (!listingId) return; // Skip if listingId is null
    await userDashboardApiDeleteSavedListing(listingId, listingType);
    onUnsaved?.();
    setSaved(false);
  }, [listingId, listingType, onUnsaved, setSaved]);

  useEffect(() => {
    if (!listingId) return; // Skip if listingId is null

    const loginChanged = initiallyLoggedInRef.current !== loggedIn;
    const actionProcessed =
      previousActionsWhileLoggedOutProcessedAt.current !==
      actionsWhileLoggedOutProcessedAt;

    // The store fetches the initial data, but it doesn't react to a logged-out action
    // being processed (e.g. a guest save replayed after signup), so still refetch on it.
    const shouldRefetch = searchSavedListingsStore
      ? actionProcessed
      : loginChanged || actionProcessed;

    if (shouldRefetch) {
      let ignore = false;
      void userDashboardApiClientFetchListingExtras([listingId]).then(
        (extras) => {
          if (ignore) return;
          const updatedSaved = Boolean(extras[listingId]);
          if (latestSavedRef.current !== updatedSaved) setSaved(updatedSaved);
          return;
        },
      );
      return () => {
        ignore = true;
      };
    }
  }, [
    loggedIn,
    actionsWhileLoggedOutProcessedAt,
    searchSavedListingsStore,
    listingId,
    latestSavedRef,
    setSaved,
  ]);

  useEffect(() => {
    if (!listingId) return; // Skip if listingId is null
    if (!searchSavedListingsStore) return;

    // Sync when this hook saved
    if (saved !== Boolean(searchSavedListingsStore.getState()[listingId])) {
      searchSavedListingsStore.setState((current) => ({
        ...current,
        [listingId]: saved,
      }));
    }
  }, [latestSavedRef, listingId, saved, searchSavedListingsStore]);

  useEffect(() => {
    if (!listingId) return; // Skip if listingId is null
    if (!searchSavedListingsStore) return;

    // Sync when saved comes from store
    return searchSavedListingsStore.subscribe((newState) => {
      if (Boolean(newState[listingId]) !== latestSavedRef.current) {
        setSaved(Boolean(newState[listingId]));
      }
    });
  }, [listingId, latestSavedRef, setSaved, searchSavedListingsStore]);

  return {
    saved: saved,
    save: () => deferWithLoadingAnimation(save),
    unsave: () => deferWithLoadingAnimation(unsave),
  };
}
